import React, { useState, useCallback } from 'react';
import Modal from './Modal';

const BLANK = { start_time: '', end_time: '', description: '', is_billable: true, service_product: '' };

const toISOStringWithTimezone = (dateTimeLocal) => {
  if (!dateTimeLocal) return null;
  return new Date(dateTimeLocal).toISOString();
};

/**
 * Standalone "Log Time Entry" form, extracted from
 * TimeExpenseManagement.js's "Add Time Entry" form so it can be embedded
 * inline (locked to one case/client) instead of only reachable from the
 * global Time & Expense Management page. Mirrors that form's behavior of
 * refreshing invoices after creation (a WIP time entry can generate/update
 * a draft invoice).
 */
export default function LogTimeEntryModal({
  open,
  onClose,
  onCreated,
  authAxios,
  showFormMessage,
  caseId,
  clientId,
  fetchInvoices,
  serviceProducts = [],
}) {
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  const fld = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await authAxios.post('time-entries/', {
        ...form,
        case: caseId || null,
        client: clientId || null,
        service_product: form.service_product || null,
        start_time: toISOStringWithTimezone(form.start_time),
        end_time: toISOStringWithTimezone(form.end_time),
      });
      showFormMessage?.('success', 'Time entry added successfully! A draft invoice has been generated.');
      setForm(BLANK);
      onCreated?.();
      await fetchInvoices?.();
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data ? JSON.stringify(err.response.data) : (err?.message || 'Failed to add time entry.');
      showFormMessage?.('danger', `Failed to add time entry. ${msg}`);
    } finally {
      setSaving(false);
    }
  }, [authAxios, form, caseId, clientId, showFormMessage, onCreated, onClose, fetchInvoices]);

  return (
    <Modal open={open} onClose={onClose} title="Log Time Entry" maxWidth="max-w-lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Start</label>
            <input type="datetime-local" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm"
              name="start_time" value={form.start_time} onChange={fld} required autoFocus />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">End</label>
            <input type="datetime-local" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm"
              name="end_time" value={form.end_time} onChange={fld} required />
          </div>
        </div>
        {serviceProducts.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700">Service/Product (for billing)</label>
            <select className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm"
              name="service_product" value={form.service_product} onChange={fld}>
              <option value="">None</option>
              {serviceProducts.map(sp => <option key={sp.id} value={sp.id}>{sp.name}</option>)}
            </select>
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700">Description</label>
          <textarea className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm" rows={3}
            name="description" value={form.description} onChange={fld} required />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            name="is_billable" checked={form.is_billable} onChange={fld} />
          Billable
        </label>
        <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
          <button type="submit" disabled={saving} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-60">
            {saving ? 'Logging…' : 'Log Time Entry'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
